<template>
  <q-dialog
    ref="dialog"
    @hide="onDialogHide"
  >
    <q-card
      class="q-dialog-plugin"
      data-e2e="dialog-suggest-targets"
    >
      <q-card-section class="row">
        <div
          class="text-h6 ellipsis col"
        >
          {{ $t('editProgram.modals.suggestTargets.textSuggestTargets') }}
        </div>
        <q-btn
          v-if="userIsAdmin"
          flat
          round
          dense
          size="sm"
          icon="settings"
          color="primary"
          @click="onToggleSettings"
        />
      </q-card-section>
      <q-card-section class="row q-col-gutter-md">
        <q-input
          v-if="showSettings"
          v-model="systemInstructions"
          :label="$t('editProgram.modals.suggestTargets.labelSystem')"
          outlined
          type="textarea"
          class="col-12"
        />
        <div class="col-12 text-caption">
          {{ $t('editProgram.modals.suggestTargets.textDescription') }}
        </div>
        <q-input
          v-model="targetQuery"
          outlined
          :placeholder="$t('editProgram.modals.suggestTargets.placeholderQuery')"
          type="textarea"
          class="col-12"
          data-e2e="input-suggest-targets-query"
        />
        <div class="col-12">
          <q-btn
            :label="$t('common.buttonSubmit')"
            color="primary"
            stretch
            :loading="isLoading"
            :disable="!isSubscriptionPremium"
            class="full-width"
            data-e2e="button-submit-suggest-targets"
            @click="onSubmitQuery"
          >
            <q-badge
              v-if="!isSubscriptionPremium"
              floating
              color="transparent"
              transparent
              outline
            >
              <q-icon
                name="workspace_premium"
                color="dark"
              />
              <q-tooltip
                v-if="!isSubscriptionPremium"
              >
                {{ $t('premiumUpgrade.upgradeHint') }}
              </q-tooltip>
            </q-badge>
          </q-btn>
        </div>
        <q-input
          v-model="targetsRaw"
          filled
          type="textarea"
          class="col-12"
          data-e2e="input-suggest-targets-output"
        />
      </q-card-section>
      <q-card-actions align="right">
        <q-btn
          flat
          :label="$t('common.buttonCancel')"
          color="secondary"
          @click="onCancelClick"
        />
        <q-btn
          flat
          :label="$t('editProgram.modals.importTargets.buttonImport')"
          :disable="!targetsRaw"
          color="primary"
          data-e2e="button-import-suggest-targets"
          @click="onOKClick"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<script>
import { callFirebaseFunction } from 'boot/firebase';
import { chain, trim } from 'lodash';
import { mapState } from 'pinia';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { aiErrorMessageKey } from 'src/utils/aiErrors';

export default {
  name: 'ImportTargets',

  mixins: [Auth, Interactions],

  props: {
    // ...your custom props
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      isLoading: false,
      showSettings: false,
      targetQuery: '',
      systemInstructions: '',
      targetsRaw: '',
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'userIsAdmin', 'isSubscriptionPremium']),
  },

  methods: {
    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    onOKClick() {
      // on OK, it is REQUIRED to
      // emit "ok" event (with optional payload)
      // before hiding the QDialog
      const targets = chain(this.targetsRaw)
        .split('\n')
        .map((target) => trim(target))
        .compact()
        .value();

      this.$emit('ok', targets);
      // or with payload: this.$emit('ok', { ... })

      // then hiding dialog
      this.hide();
    },

    onCancelClick() {
      // we just need to hide dialog
      this.hide();
    },

    onToggleSettings() {
      if (!this.userIsAdmin) {
        return;
      }
      this.showSettings = !this.showSettings;
    },

    async onSubmitQuery() {
      this.isLoading = true;
      try {
        const params = {
          clinicId: this.userDetails.clinic,
          query: this.targetQuery,
        };
        if (this.userIsAdmin && this.systemInstructions) {
          params.overwriteSystem = this.systemInstructions;
        }

        const res = await callFirebaseFunction('suggestTargets', params);
        if (chain(res).get(['data', 'content']).isString().value()) {
          this.targetsRaw = res.data.content;
        }
      } catch (err) {
        console.error(err);
        this.notifyNegative(this.$t(aiErrorMessageKey(err, 'common.errors.generic')));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
